import { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";

import { makeMatcher } from "../../Sidebar/FilterInput/makeMatcher";
import { isFilterEmpty, type KeywordFilter } from "../filter";
import {
  FilterResult,
  MessagePathFilterResult,
  TopicFilterResult,
} from "../filterResults";

type FilterResults = FilterResult[];

export class TopicFilterEngine {
  #topicRecords: TopicRecord[];
  #messagePathRecords: MessagePathRecord[];
  #allFilterResults: FilterResults = [];

  constructor(topicRecords: TopicRecord[]) {
    this.#topicRecords = topicRecords;

    this.#messagePathRecords = topicRecords.reduce<MessagePathRecord[]>(
      (messagePaths, topic) => {
        return messagePaths.concat(...topic.message_paths);
      },
      [],
    );

    this.#allFilterResults = [
      ...this.#topicRecords.map((topic): TopicFilterResult => ({
        type: "topic",
        topic,
      })),
      ...this.#messagePathRecords.map(
        (messagePath): MessagePathFilterResult => ({
          type: "messagePath",
          messagePath,
        }),
      ),
    ];
  }

  filter(filter: KeywordFilter): FilterResults {
    if (isFilterEmpty(filter)) {
      return this.#allFilterResults;
    }

    const matcher = makeMatcher(filter);

    const topicMatches: TopicFilterResult[] = [];
    for (const topic of this.#topicRecords) {
      const result = matcher(topic.topic_name);
      if (!result.matches) {
        continue;
      }
      topicMatches.push({
        type: "topic",
        topic,
        match: result.position ? { position: result.position } : undefined,
      });
    }

    const messagePathMatches: MessagePathFilterResult[] = [];
    for (const messagePath of this.#messagePathRecords) {
      // Only process the last part of the path because nested message paths
      // contain the full path (e.g. ["foo", "bar"] and ["foo", "bar", "baz"])
      const path = messagePath.path_in_schema.at(-1) ?? "";

      const result = matcher(path);
      if (!result.matches) {
        continue;
      }
      messagePathMatches.push({
        type: "messagePath",
        messagePath,
        match: result.position ? { position: result.position } : undefined,
      });
    }

    return [...topicMatches, ...messagePathMatches];
  }
}
